import React from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup as MuiRadioGroup,
} from '@mui/material';
import { styled } from '@mui/material/styles';

export type RadioOption = {
  value: string;
  name: string;
};

export interface RadioGroupProps {
  name?: string;
  label?: string;
  value?: string;
  options: RadioOption[];
  required?: boolean;
  disabled?: boolean;
  validationText?: string;
  warningText?: string;
  row?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
}

const RadioGroupWrapper = styled(Box)({
  width: '100%',
  margin: 0,
  display: 'flex',
});

const StyledFormControl = styled(FormControl)({
  width: '100%',
});

const StyledMuiRadioGroup = styled(MuiRadioGroup, {
  shouldForwardProp: (prop) => prop !== 'row',
})<{ row?: boolean }>(({ theme, row }) => ({
  ...(row
    ? {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        '& .MuiFormControlLabel-root': {
          marginRight: theme.spacing(2),
          marginBottom: 0,
          minWidth: 'auto',
          [theme.breakpoints.down('sm')]: {
            marginRight: theme.spacing(1),
          },
        },
      }
    : {
        display: 'flex',
        flexDirection: 'column',
        '& .MuiFormControlLabel-root': {
          margin: 0,
        },
      }),
  marginBottom: 0,
}));

const ValidationBox = styled(Box)({
  minHeight: '20px',
  marginTop: 0,
});

export const RadioGroupField: React.FC<RadioGroupProps> = React.memo(
  ({
    name,
    label,
    value,
    options,
    required = false,
    disabled = false,
    validationText,
    warningText,
    row = false,
    onChange,
    onBlur,
  }) => {
    const error = validationText !== undefined && validationText !== '';
    const hasWarning = warningText !== undefined && warningText !== '';

    return (
      <RadioGroupWrapper>
        <StyledFormControl
          required={required}
          disabled={disabled}
          error={error}
          fullWidth
        >
          {label && <FormLabel>{label}</FormLabel>}
          <StyledMuiRadioGroup
            name={name}
            value={value === undefined ? '' : value}
            onChange={onChange}
            onBlur={onBlur}
            row={row}
          >
            {options.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio data-testid={`${name}-${option.value}`} />}
                label={option.name}
                disabled={disabled}
              />
            ))}
          </StyledMuiRadioGroup>

          <ValidationBox>
            <FormHelperText
              sx={{ color: hasWarning ? 'warning.main' : undefined }}
            >
              {validationText || warningText || ' '}
            </FormHelperText>
          </ValidationBox>
        </StyledFormControl>
      </RadioGroupWrapper>
    );
  },
);
